import { useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import TopBanner from "@/components/TopBanner";

const LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/boutique", label: "Boutique" },
  { to: "/a-propos", label: "À propos" },
  { to: "/suivi", label: "Suivi de commande" },
];

const Navbar = () => {
  const { count, setOpen } = useCart();
  const headerRef = useRef(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    let raf = null;
    const apply = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
      });
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  const linkClass = ({ isActive }) =>
    `text-sm font-medium tracking-wide transition-colors duration-300 hover:text-terracotta ${
      isActive ? "text-terracotta" : "text-bark/70"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `text-[11px] font-bold uppercase tracking-[0.12em] transition-colors duration-300 hover:text-terracotta ${
      isActive ? "text-terracotta" : "text-bark/60"
    }`;

  return (
    <header ref={headerRef} data-testid="main-navbar" className="fixed top-0 z-40 w-full border-b border-clay bg-sand/80 backdrop-blur-xl">
      <TopBanner />
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src="/logo-mark.png" alt="Sama Keur Shop" className="h-12 w-auto rounded-lg" />
        </Link>

        <nav className="hidden items-center gap-10 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className={linkClass}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-bark/40 transition-colors duration-300 hover:text-terracotta md:block"
          >
            Admin
          </Link>
          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="relative flex h-11 w-11 items-center justify-center rounded-full border border-clay bg-cream text-bark transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
            aria-label="Ouvrir le panier"
          >
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-terracotta text-[11px] font-bold text-sand">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Liens toujours visibles sur mobile — plus de menu hamburger */}
      <nav data-testid="nav-mobile-links" className="flex items-center justify-center gap-6 border-t border-clay/60 px-4 py-3 md:hidden">
        {LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
            className={mobileLinkClass}
          >
            {l.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
};

export default Navbar;
